import { useEffect, useState } from 'react';
import { analectById, canonicalId } from '../engine/analects';

export function parseReadingProgress(raw: string | null): Record<number, string> {
  if (!raw) return {};
  const value: unknown = JSON.parse(raw);
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw Error('invalid reading record');
  const result: Record<number, string> = {};
  for (const [key, at] of Object.entries(value)) {
    const id = canonicalId(Number(key));
    if (!Number.isInteger(id) || !analectById.has(id) || typeof at !== 'string' || !Number.isFinite(Date.parse(at))) throw Error('invalid reading entry');
    result[id] = at;
  }
  return result;
}

/** Explicit reading self-check, isolated from game scores and central evidence. */
export function useReadingProgress(owner: string | null) {
  const key = `analects-reading-progress:v1:${owner || 'device'}`;
  const [snapshot, setSnapshot] = useState<{ key: string; entries: Record<number, string>; error: string }>({ key: '', entries: {}, error: '' });
  useEffect(() => {
    const load = () => {
      try { setSnapshot({ key, entries: parseReadingProgress(localStorage.getItem(key)), error: '' }); }
      catch { setSnapshot({ key, entries: {}, error: '閱讀紀錄暫時無法讀取，原資料已保留。' }); }
    };
    load();
    const changed = (event: StorageEvent) => { if (event.key === key || event.key === null) load(); };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
  }, [key]);
  const entries = snapshot.key === key ? snapshot.entries : {};
  function toggle(id: number, checked: boolean) {
    if (snapshot.key !== key || snapshot.error) return;
    try {
      // Read the latest same-owner copy so another tab's independent marks survive.
      const next = parseReadingProgress(localStorage.getItem(key));
      if (checked) next[canonicalId(id)] = new Date().toISOString(); else delete next[canonicalId(id)];
      localStorage.setItem(key, JSON.stringify(next));
      setSnapshot({ key, entries: next, error: '' });
    } catch { setSnapshot(previous => ({ ...previous, error: '尚未保存閱讀勾選，請確認瀏覽器儲存空間。' })); }
  }
  return { entries, toggle, ready: snapshot.key === key && !snapshot.error, error: snapshot.key === key ? snapshot.error : '' };
}
export type ReadingProgress = ReturnType<typeof useReadingProgress>;
